import { useRef, useState, useEffect } from "react";
import { ChartCard } from "./chart-card";
import { ProgressList, type SortMode } from "./progress-list";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { TabsContent } from "@/components/ui/tabs";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	Legend,
} from "recharts";
import { Download } from "lucide-react";
import {
	type StatisticsByProvince,
	type PaginatedResponse,
	type StatisticsFilter,
} from "@/lib/statistics-api.service";
import statisticsAPIService from "@/lib/statistics-api.service";
import { useAuth } from "@/global-hooks/useAuth";
import { COLORS, downloadChartPng, toAPIFilter } from "./utils";
import {
	BAR_VALUE_LABEL,
	NO_ANIMATION,
	CATEGORY_AXIS_EXTRA_HEIGHT,
	CATEGORY_AXIS_HEIGHT,
	WrappedCategoryTick,
	chartMinWidth,
} from "./chart-ticks";
import {
	StatisticsFilterControls,
	type StatisticsFilterType,
	type StatisticsFilters,
} from "./StatisticsControls";

interface ProvincesTabProps {
	filters?: StatisticsFilters;
	onFiltersChange?: (filters: StatisticsFilters) => void;
	/** Tăng lên mỗi lần trang yêu cầu tải lại; đổi giá trị là fetch lại. */
	refreshToken?: number;
}

/**
 * Lấy hết tỉnh/thành trong một lần gọi thay vì phân trang.
 *
 * Số hạng mục ở đây bị chặn bởi bản đồ hành chính, không bởi số sinh viên: 34
 * tỉnh/thành từ 2026, 63 ở dữ liệu khoá cũ, cộng vài mã đặc biệt. Một trăm phủ
 * dư cả hai và vẫn là một phản hồi nhỏ. Không bỏ hẳn tham số vì server mặc định
 * `limit = 50`, đủ để cắt mất phần đuôi của khoá 2025 mà không báo gì.
 */
const PROVINCE_FETCH_LIMIT = 100;

/**
 * Nhận ra TP.HCM từ tên tỉnh đã chuẩn hoá phía server.
 *
 * Không so mã tỉnh: GSO đánh HCM là 79 còn MOET đánh 02, và hai khoá dữ liệu
 * dùng hai bảng mã khác nhau. Tên thì backend đã canonicalize về "Hồ Chí Minh"
 * cho mọi biến thể ("Tp. Hồ Chí Minh", "TP.HCM"), nên so tên là ổn định hơn.
 * Vẫn bỏ dấu và bỏ tiền tố "tp" phòng khi dữ liệu cũ lọt qua chưa chuẩn hoá.
 */
const isHoChiMinh = (provinceName: string) =>
	provinceName
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "")
		.toLowerCase()
		.replace(/^tp\.?\s*/, "")
		.trim() === "ho chi minh";

export default function ProvincesTab({
	filters = {},
	onFiltersChange,
	refreshToken = 0,
}: ProvincesTabProps = {}) {
	const provinceRef = useRef<HTMLDivElement>(null);
	const { user } = useAuth();

	const [filterType, setFilterType] = useState<StatisticsFilterType>("total");
	const [provinceData, setProvinceData] = useState<PaginatedResponse<StatisticsByProvince> | null>(
		null,
	);
	const [isLoading, setIsLoading] = useState(false);
	const [sortBy, setSortBy] = useState<SortMode>("count");
	// TP.HCM chiếm gần một nửa số sinh viên, nên cột của nó ép 24 tỉnh còn lại
	// xuống sát trục. Bỏ nó khỏi biểu đồ (không đụng danh sách chi tiết bên dưới)
	// là cách xem phần đuôi mà không phải bóp méo thang đo bằng log.
	const [hideHoChiMinh, setHideHoChiMinh] = useState(false);

	// Fetch data with filters
	const fetchProvinceData = async (type: StatisticsFilterType, limit?: number, offset?: number) => {
		setIsLoading(true);
		try {
			const filter: StatisticsFilter = toAPIFilter(filters, type);
			if (limit !== undefined) filter.limit = limit;
			if (offset !== undefined) filter.offset = offset;
			filter.sortBy = sortBy;

			const data = await statisticsAPIService.getStatsByProvince(filter);
			setProvinceData(data);
		} catch (error) {
			console.error("Error fetching province data:", error);
			// Set empty data on error
			setProvinceData({
				data: [],
				total: 0,
				page: 1,
				limit: limit || 50,
				hasNext: false,
				hasPrev: false,
			});
		} finally {
			setIsLoading(false);
		}
	};

	// Effect to fetch data when filter changes
	useEffect(() => {
		fetchProvinceData(filterType, PROVINCE_FETCH_LIMIT, 0);
		// `filters` belongs in here: the khoa dropdown and the date range live on
		// this tab too, and without it the controls change while the table does not.
		// `refreshToken` cũng vậy: nút Tải lại của trang phải làm mới cả bảng này,
		// không riêng phần số liệu do trang tự giữ.
		// `sortBy` nằm ở server nên đổi thứ tự cũng là một lần nạp lại.
	}, [filterType, filters, refreshToken, sortBy]);

	// Handle filter change
	const handleFilterChange = (type: StatisticsFilterType) => {
		setFilterType(type);
	};

	// Get chart data based on filter type
	const getChartData = () => {
		if (!provinceData?.data) return [];

		// Vẽ hết, không cắt top-N: cả nước chỉ có 34 tỉnh/thành, và biểu đồ đã cuộn
		// ngang được nên thêm cột không bóp nhỏ cột nào cả. Cắt bớt chỉ khiến người
		// xem không thấy được những tỉnh có ít sinh viên — thứ họ thường đi tìm.
		const rows = hideHoChiMinh
			? provinceData.data.filter((province) => !isHoChiMinh(province.provinceName))
			: provinceData.data;

		return rows.map((province) => {
			const chartEntry: any = {
				provinceName: province.provinceName,
				total: province.total,
			};

			switch (filterType) {
				case "enrollment_status":
					chartEntry.received = province.received;
					chartEntry.pending = province.pending;
					break;
				case "payment_status":
					chartEntry.paid = province.paid;
					chartEntry.unpaid = province.unpaid;
					break;
				case "paid_only":
					chartEntry.paid = province.paid;
					break;
				case "unpaid_only":
					chartEntry.unpaid = province.unpaid;
					break;
				case "received_only":
					chartEntry.received = province.received;
					break;
				default: // total
					chartEntry.received = province.received;
					chartEntry.pending = province.pending;
					break;
			}

			return chartEntry;
		});
	};

	// Get bars for chart based on filter type
	const getChartBars = () => {
		switch (filterType) {
			case "enrollment_status":
				return [
					<Bar
						{...NO_ANIMATION}
						key="received"
						dataKey="received"
						fill={COLORS[2]}
						name="Đã tiếp nhận"
						label={BAR_VALUE_LABEL}
					/>,
					<Bar
						{...NO_ANIMATION}
						key="pending"
						dataKey="pending"
						fill={COLORS[3]}
						name="Chưa tiếp nhận"
						label={BAR_VALUE_LABEL}
					/>,
				];
			case "payment_status":
				return [
					<Bar
						{...NO_ANIMATION}
						key="paid"
						dataKey="paid"
						fill={COLORS[4]}
						name="Đã đóng học phí"
						label={BAR_VALUE_LABEL}
					/>,
					<Bar
						{...NO_ANIMATION}
						key="unpaid"
						dataKey="unpaid"
						fill={COLORS[5]}
						name="Chưa đóng học phí"
						label={BAR_VALUE_LABEL}
					/>,
				];
			case "paid_only":
				return [
					<Bar
						{...NO_ANIMATION}
						key="paid"
						dataKey="paid"
						fill={COLORS[4]}
						name="Đã đóng học phí"
						label={BAR_VALUE_LABEL}
					/>,
				];
			case "unpaid_only":
				return [
					<Bar
						{...NO_ANIMATION}
						key="unpaid"
						dataKey="unpaid"
						fill={COLORS[5]}
						name="Chưa đóng học phí"
						label={BAR_VALUE_LABEL}
					/>,
				];
			case "received_only":
				return [
					<Bar
						{...NO_ANIMATION}
						key="received"
						dataKey="received"
						fill={COLORS[2]}
						name="Đã tiếp nhận"
						label={BAR_VALUE_LABEL}
					/>,
				];
			default: // total
				return [
					<Bar
						{...NO_ANIMATION}
						key="received"
						dataKey="received"
						fill={COLORS[0]}
						name="Đã tiếp nhận"
						label={BAR_VALUE_LABEL}
					/>,
					<Bar
						{...NO_ANIMATION}
						key="pending"
						dataKey="pending"
						fill={COLORS[1]}
						name="Chưa tiếp nhận"
						label={BAR_VALUE_LABEL}
					/>,
				];
		}
	};

	if (isLoading && !provinceData) {
		return (
			<TabsContent value="provinces">
				<div className="flex items-center justify-center h-64">
					<p>Đang tải dữ liệu...</p>
				</div>
			</TabsContent>
		);
	}

	return (
		<TabsContent value="provinces">
			<div ref={provinceRef} className="space-y-6">
				<div className="flex justify-between items-center">
					<h2 className="text-2xl font-semibold">Thống kê theo tỉnh thành</h2>
					<div className="flex items-center gap-2">
						{(user?.role === "admin" || user?.role === "manager") && (
							<Button
								onClick={async () => {
									try {
										await statisticsAPIService.exportProvinceStatsAsCSV(
											toAPIFilter(filters, filterType),
										);
									} catch (error) {
										console.error("Error exporting CSV:", error);
										alert("Không thể tải CSV. Vui lòng thử lại.");
									}
								}}
								variant="outline"
								size="sm"
								className="flex items-center gap-2"
							>
								<Download className="h-4 w-4" />
								Tải CSV
							</Button>
						)}
						<Button
							onClick={() => downloadChartPng(provinceRef, "thong-ke-theo-tinh")}
							variant="outline"
							size="sm"
							className="flex items-center gap-2"
						>
							<Download className="h-4 w-4" />
							Tải PNG
						</Button>
					</div>
				</div>

				<StatisticsFilterControls
					filterType={filterType}
					onFilterChange={handleFilterChange}
					onChartLimitChange={() => {}}
					showChartLimit={false}
					filters={filters}
					onFiltersChange={onFiltersChange}
					showFilters={true}
				/>

				<ChartCard
					title="Thống kê theo tỉnh thành"
					filename="thong-ke-theo-tinh-thanh"
					description={
						hideHoChiMinh
							? `Hiển thị ${getChartData().length} tỉnh thành, đã ẩn TP.HCM`
							: `Hiển thị toàn bộ ${getChartData().length} tỉnh thành có sinh viên`
					}
					actions={
						<label className="flex h-8 cursor-pointer select-none items-center gap-2 text-sm text-muted-foreground">
							<Checkbox
								checked={hideHoChiMinh}
								onCheckedChange={(checked) => setHideHoChiMinh(checked === true)}
								aria-label="Ẩn TP.HCM khỏi biểu đồ"
							/>
							Ẩn TP.HCM
						</label>
					}
				>
					<div className="overflow-x-auto">
						<div style={{ minWidth: chartMinWidth(getChartData().length) }}>
							<ResponsiveContainer width="100%" height={400 + CATEGORY_AXIS_EXTRA_HEIGHT}>
								<BarChart
									data={getChartData()}
									margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
								>
									<CartesianGrid strokeDasharray="3 3" />
									<XAxis
										dataKey="provinceName"
										interval={0}
										tick={<WrappedCategoryTick />}
										height={CATEGORY_AXIS_HEIGHT}
									/>
									<YAxis />
									<Tooltip />
									<Legend />
									{getChartBars()}
								</BarChart>
							</ResponsiveContainer>
						</div>
					</div>
				</ChartCard>

				<ProgressList
					title="Tiến độ theo tỉnh/thành"
					description={`${provinceData?.data.length ?? 0} tỉnh thành có sinh viên`}
					remainderLabel="Chưa nộp hồ sơ, chưa đóng phí"
					sortBy={sortBy}
					onSortByChange={setSortBy}
					isRefreshing={isLoading}
					rows={(provinceData?.data ?? []).map((province) => ({
						id: province.provinceCode,
						label: province.provinceName,
						sublabel: `Mã tỉnh: ${province.provinceCode}`,
						total: province.total,
						primary: province.received,
						segments: [
							{
								key: "received",
								label: "Đã tiếp nhận",
								value: province.received,
								className: "bg-green-600",
							},
							{
								key: "paidButNotReceived",
								label: "Đã đóng phí, chưa tiếp nhận",
								value: province.paidButNotReceived,
								className: "bg-green-300 dark:bg-green-800",
							},
						],
						details: [
							{ label: "Tổng", value: province.total },
							{
								label: "Đã tiếp nhận",
								value: province.received,
								className: "text-green-700 dark:text-green-400",
							},
							{
								label: "Chưa tiếp nhận",
								value: province.pending,
								className: "text-orange-700 dark:text-orange-400",
							},
							{
								label: "Đã đóng học phí",
								value: province.paid,
								className: "text-blue-600 dark:text-blue-400",
							},
							{
								label: "Chưa đóng học phí",
								value: province.unpaid,
								className: "text-red-600 dark:text-red-400",
							},
							{
								label: "Đã đóng phí chưa tiếp nhận",
								value: province.paidButNotReceived,
								className: "text-orange-700 dark:text-orange-400",
							},
							{
								label: "Chưa nộp, chưa đóng phí",
								value: province.notReceivedAndNotPaid,
								className: "text-muted-foreground",
							},
							{ label: "Nam", value: province.male, className: "text-blue-600 dark:text-blue-400" },
							{
								label: "Nữ",
								value: province.female,
								className: "text-pink-600 dark:text-pink-400",
							},
						],
					}))}
				/>
			</div>
		</TabsContent>
	);
}
